import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";
import type {
  AccountJournal,
  JournalFormData,
  JournalType,
} from "~/types/contabilidad";
import { JOURNAL_TYPE_LABELS } from "~/types/contabilidad";

const formSchema = z.object({
  company_id: z.string().min(1, "Seleccione una empresa"),
  code: z.string().min(1, "El código es requerido"),
  name: z.string().min(2, "El nombre debe tener al menos 2 caracteres"),
  type: z.enum(["general", "venta", "compra", "banco"], {
    required_error: "Seleccione un tipo",
  }),
});

interface DiarioFormProps {
  initialData?: AccountJournal;
  onSubmit: (data: JournalFormData) => void;
  isLoading?: boolean;
  submitLabel?: string;
}

export function DiarioForm({
  initialData,
  onSubmit,
  isLoading = false,
  submitLabel = "Guardar",
}: DiarioFormProps) {
  const { data: companies = [], isLoading: isLoadingCompanies } =
    useCompanies();

  const form = useForm<JournalFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      company_id: initialData?.company_id ?? "",
      code: initialData?.code ?? "",
      name: initialData?.name ?? "",
      type: initialData?.type ?? "general",
    },
  });

  const handleSubmit = (data: JournalFormData) => {
    onSubmit(data);
  };

  const journalTypes: JournalType[] = ["general", "venta", "compra", "banco"];

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="company_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Empresa *</FormLabel>
              <Select
                onValueChange={field.onChange}
                defaultValue={field.value}
                disabled={isLoadingCompanies || !!initialData}
              >
                <FormControl>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Seleccione una empresa" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Código *</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Ej: VTA, CMP, BAN"
                    className="rounded-xl"
                    {...field}
                    onChange={(e) =>
                      field.onChange(e.target.value.toUpperCase())
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tipo *</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Seleccione tipo" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {journalTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {JOURNAL_TYPE_LABELS[type]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nombre *</FormLabel>
              <FormControl>
                <Input
                  placeholder="Nombre del diario"
                  className="rounded-xl"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end pt-4">
          <Button
            type="submit"
            className="rounded-full px-6"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Guardando...
              </>
            ) : (
              submitLabel
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
